/**
 * FilterPanel — the only allowed filter chrome for ERP list/report pages.
 *
 * Layout contract (do not break):
 * 1. Header (title + active count)
 * 2. Date band — full width when dateRange is set (never inside the fields grid)
 * 3. Fields grid — search + FilterPanelField children only (never date, never actions)
 * 4. Optional footer extras (switches / comparison)
 * 5. Active chips
 * 6. Actions row — Clear + Apply / extraActions only (never inline with fields)
 */
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp, Filter, Search, X } from 'lucide-react'
import { cn } from '../lib/utils'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Card, CardContent } from './ui/card'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { DateRangeFilterField } from './date-range-filter-field'
import { ClearFiltersButton } from './clear-filters-button'

export type FilterPanelChip = {
  id: string
  label: string
  onRemove?: () => void
}

export type FilterPanelDateRange = {
  from: string
  to: string
  onChange: (next: { from: string; to: string }) => void
  showPresets?: boolean
  includeAllTimePreset?: boolean
  label?: string
}

export type FilterPanelSearch = {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  label?: string
  onSubmit?: () => void
}

type FilterPanelPreset = 'all' | 'today' | '7d' | '30d' | '90d' | 'custom'

function toLocalDateString(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function todayLocal(): string {
  return toLocalDateString(new Date())
}

function daysAgoLocal(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return toLocalDateString(d)
}

function presetToRange(preset: FilterPanelPreset): { from: string; to: string } {
  if (preset === 'all' || preset === 'custom') return { from: '', to: '' }
  const to = todayLocal()
  if (preset === 'today') return { from: to, to }
  if (preset === '7d') return { from: daysAgoLocal(6), to }
  if (preset === '30d') return { from: daysAgoLocal(29), to }
  return { from: daysAgoLocal(89), to }
}

function detectPreset(from: string, to: string): FilterPanelPreset {
  if (!from && !to) return 'all'
  const today = todayLocal()
  if (from === today && to === today) return 'today'
  const r7 = presetToRange('7d')
  if (r7.from === from && r7.to === to) return '7d'
  const r30 = presetToRange('30d')
  if (r30.from === from && r30.to === to) return '30d'
  const r90 = presetToRange('90d')
  if (r90.from === from && r90.to === to) return '90d'
  return 'custom'
}

const QUICK_PRESETS: FilterPanelPreset[] = ['all', 'today', '7d', '30d', '90d']
const QUICK_PRESETS_WITHOUT_ALL: FilterPanelPreset[] = ['today', '7d', '30d', '90d']

export function FilterPanelField({
  label,
  className,
  children,
  span,
}: {
  label?: string
  className?: string
  children: React.ReactNode
  /** Column span hints for the dense grid */
  span?: 1 | 2 | 3 | 4
}) {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-1',
        span === 2 && 'sm:col-span-2',
        span === 3 && 'lg:col-span-3',
        span === 4 && 'sm:col-span-2 lg:col-span-3 xl:col-span-4',
        className,
      )}
    >
      {label ? <Label className="text-xs font-medium text-muted-foreground">{label}</Label> : null}
      {children}
    </div>
  )
}

export type FilterPanelProps = {
  title?: string
  description?: string
  icon?: React.ReactNode
  collapsible?: boolean
  defaultOpen?: boolean
  activeCount?: number
  dateRange?: FilterPanelDateRange
  search?: FilterPanelSearch
  chips?: FilterPanelChip[]
  onApply?: () => void
  applyDisabled?: boolean
  onClear: () => void
  applyLabel?: string
  /** Prefer FilterPanelField children — never put date/actions here */
  children?: React.ReactNode
  /** Apply/Clear companions only (export buttons, etc.) — rendered in the actions row */
  extraActions?: React.ReactNode
  className?: string
  /** Switches / comparison — below the fields grid, above chips */
  footer?: React.ReactNode
  /** When true, wraps content without Card chrome (used by FilterBar inside an outer card) */
  bare?: boolean
}

export function FilterPanel({
  title,
  description,
  icon,
  collapsible = true,
  defaultOpen = false,
  activeCount = 0,
  dateRange,
  search,
  chips = [],
  onApply,
  applyDisabled = false,
  onClear,
  applyLabel,
  extraActions,
  children,
  className,
  footer,
  bare = false,
}: FilterPanelProps) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(defaultOpen)
  const showBody = !collapsible || open

  const resolvedTitle = title ?? t('ui.filters.title')
  const activePreset = useMemo(
    () => (dateRange ? detectPreset(dateRange.from, dateRange.to) : 'all'),
    [dateRange?.from, dateRange?.to],
  )
  const includeAllTimePreset = dateRange?.includeAllTimePreset !== false
  const quickPresets = includeAllTimePreset ? QUICK_PRESETS : QUICK_PRESETS_WITHOUT_ALL

  const presetLabels: Record<FilterPanelPreset, string> = {
    all: t('ui.dateRange.presets.all'),
    today: t('ui.filters.presets.today'),
    '7d': t('ui.filters.presets.7d'),
    '30d': t('ui.filters.presets.30d'),
    '90d': t('ui.filters.presets.90d'),
    custom: t('ui.filters.presets.custom'),
  }

  const header = (
    <>
      <span className="shrink-0 text-muted-foreground [&_svg]:h-4 [&_svg]:w-4">
        {icon ?? <Filter className="h-4 w-4" aria-hidden />}
      </span>
      <div className="min-w-0 flex-1 text-start">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold text-foreground">{resolvedTitle}</h2>
          {activeCount > 0 ? (
            <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-medium">
              {t('ui.filters.activeCount', { count: activeCount })}
            </Badge>
          ) : null}
        </div>
        {description && showBody ? (
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {collapsible ? (
        <span className="shrink-0 text-muted-foreground" aria-hidden>
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      ) : null}
    </>
  )

  const chipsRow =
    chips.length > 0 ? (
      <div className="flex flex-wrap gap-1.5">
        {chips.map((chip) => (
          <button
            key={chip.id}
            type="button"
            onClick={chip.onRemove}
            disabled={!chip.onRemove}
            className={cn(
              'inline-flex max-w-full items-center gap-1 rounded-full border border-border-subtle bg-muted/50 px-2.5 py-1 text-[11px] font-medium text-foreground',
              chip.onRemove && 'hover:bg-muted hover:text-destructive',
            )}
          >
            <span className="truncate">{chip.label}</span>
            {chip.onRemove ? <X className="h-3 w-3 shrink-0 opacity-70" aria-hidden /> : null}
          </button>
        ))}
      </div>
    ) : null

  const body = showBody ? (
    <div className="space-y-4">
      {/* 2. Date band — full width only */}
      {dateRange ? (
        <div className="w-full space-y-2.5 rounded-2xl border border-border-subtle bg-muted/20 p-3">
          <DateRangeFilterField
            label={dateRange.label}
            value={{ from: dateRange.from || null, to: dateRange.to || null }}
            onChange={({ from, to }) => dateRange.onChange({ from: from ?? '', to: to ?? '' })}
            includeAllTimePreset={includeAllTimePreset}
            className="w-full max-w-none"
          />
          {dateRange.showPresets !== false ? (
            <div className="flex flex-wrap gap-1 rounded-xl border border-border-subtle bg-background/60 p-1">
              {quickPresets.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => dateRange.onChange(presetToRange(preset))}
                  className={cn(
                    'rounded-lg px-3 py-1.5 text-xs font-medium transition-all',
                    activePreset === preset
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  {presetLabels[preset]}
                </button>
              ))}
              {activePreset === 'custom' ? (
                <span className="inline-flex items-center rounded-lg bg-muted px-3 py-1.5 text-xs font-medium text-foreground">
                  {presetLabels.custom}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* 3. Fields grid — search + filters only */}
      {(search || children) ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {search ? (
            <FilterPanelField
              label={search.label ?? t('ui.searchLabel')}
              /* Full-width first row so long AR/FR placeholders are not clipped */
              span={4}
            >
              <div className="relative min-w-0">
                <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <Input
                  value={search.value}
                  onChange={(e) => search.onChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') search.onSubmit?.()
                  }}
                  placeholder={search.placeholder ?? t('ui.searchPlaceholder')}
                  title={search.placeholder ?? t('ui.searchPlaceholder')}
                  className="h-10 min-h-10 w-full min-w-0 rounded-xl border-border-subtle pe-3 ps-10 text-sm shadow-sm"
                />
              </div>
            </FilterPanelField>
          ) : null}
          {children}
        </div>
      ) : null}

      {/* 4. Extras */}
      {footer}

      {/* 5. Chips */}
      {chipsRow}

      {/* 6. Actions — Clear + Apply only */}
      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border-subtle pt-3">
        <ClearFiltersButton count={activeCount} onClear={onClear} />
        {extraActions}
        {onApply ? (
          <Button
            type="button"
            size="sm"
            className="rounded-xl"
            onClick={onApply}
            disabled={applyDisabled}
          >
            {applyLabel ?? t('ui.filters.apply')}
          </Button>
        ) : null}
      </div>
    </div>
  ) : null

  if (bare) {
    return (
      <div className={cn(className)} dir={i18n.dir()}>
        {collapsible ? (
          <button
            type="button"
            className={cn(
              'mb-4 flex w-full items-center gap-3 border-b border-border-subtle pb-3 text-start',
              !showBody && 'mb-0 border-b-0 pb-0',
            )}
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {header}
          </button>
        ) : title || description ? (
          <div className="mb-4 flex items-center gap-3 border-b border-border-subtle pb-3">{header}</div>
        ) : null}
        {!showBody && chipsRow ? <div className="mt-3">{chipsRow}</div> : null}
        {body}
      </div>
    )
  }

  return (
    <Card className={cn('admin-surface-card mb-5', className)} dir={i18n.dir()}>
      <CardContent className={cn('p-5', !showBody && 'pb-4')}>
        {collapsible ? (
          <button
            type="button"
            className={cn(
              'flex w-full items-center gap-3 text-start',
              showBody && 'mb-4 border-b border-border-subtle pb-3',
            )}
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {header}
          </button>
        ) : (
          <div className="mb-4 flex items-center gap-3 border-b border-border-subtle pb-3">{header}</div>
        )}

        {!showBody && chipsRow ? <div className="mt-3">{chipsRow}</div> : null}
        {body}
      </CardContent>
    </Card>
  )
}
